import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

/**
 * Merge ticket modal controller.
 *
 * data-controller="support--merge-ticket"
 * data-support--merge-ticket-search-url-value="/support/tickets"
 * data-support--merge-ticket-current-id-value="uuid"
 *
 * Uses two strategies to find a target ticket:
 *   1. On open: loads AI-similar tickets from the /similar JSON endpoint
 *   2. Search input: fetches tickets from the /search JSON endpoint
 *
 * Targets:
 *   modal, searchInput, results, loading, preview, suggestions,
 *   previewNumber, previewSubject, previewMeta,
 *   targetId, confirmBtn, form
 */
export default class extends Controller {
  static targets = [
    "modal",
    "searchInput",
    "results",
    "loading",
    "preview",
    "suggestions",
    "previewNumber",
    "previewSubject",
    "previewMeta",
    "targetId",
    "confirmBtn",
    "form",
  ]

  static values = {
    searchUrl: String, // base tickets path (e.g. /s:group:branch/support/tickets)
    currentId: String, // current ticket id — excluded from results
  }

  connect() {
    this._searchTimeout = null
    this._selectedId = null
  }

  open() {
    this.modalTarget.classList.remove("hidden")
    this.resetState()
    this._loadSuggestions()
    // Focus the search input after a frame so the modal is visible
    requestAnimationFrame(() => this.searchInputTarget.focus())
  }

  close() {
    this.modalTarget.classList.add("hidden")
    this.resetState()
  }

  resetState() {
    this.searchInputTarget.value = ""
    this.resultsTarget.classList.add("hidden")
    this.resultsTarget.innerHTML = ""
    this.loadingTarget.classList.add("hidden")
    this.previewTarget.classList.add("hidden")
    if (this.hasSuggestionsTarget)
      this.suggestionsTarget.classList.add("hidden")
    this.targetIdTarget.value = ""
    this.confirmBtnTarget.disabled = true
    this._selectedId = null
  }

  search() {
    const query = this.searchInputTarget.value.trim()

    // Clear previous timeout
    if (this._searchTimeout) clearTimeout(this._searchTimeout)

    if (query.length < 2) {
      this.resultsTarget.classList.add("hidden")
      this.resultsTarget.innerHTML = ""
      this.loadingTarget.classList.add("hidden")
      // Show suggestions again when search is cleared
      if (this.hasSuggestionsTarget)
        this.suggestionsTarget.classList.remove("hidden")
      return
    }

    // Hide suggestions while searching
    if (this.hasSuggestionsTarget)
      this.suggestionsTarget.classList.add("hidden")

    // Debounce 300ms
    this._searchTimeout = setTimeout(() => this._performSearch(query), 300)
  }

  async _loadSuggestions() {
    if (!this.hasSuggestionsTarget) return

    try {
      const url = `${this.searchUrlValue}/${this.currentIdValue}/similar`
      const response = await get(url, { responseKind: "json" })

      if (!response.ok) return

      const tickets = await response.json
      if (!tickets.length) return

      this._renderSuggestions(tickets)
    } catch {
      // Silently fail — suggestions are optional
    }
  }

  _renderSuggestions(tickets) {
    const html = tickets
      .map(
        (t) => `
      <button type="button"
              class="w-full flex items-start gap-3 px-3 py-2 rounded-lg hover:bg-muted/60 transition-colors text-left group"
              data-action="click->support--merge-ticket#selectTarget"
              data-ticket-id="${t.id}"
              data-ticket-number="${this._escapeHtml(t.ticket_number || "")}"
              data-ticket-subject="${this._escapeHtml(t.subject || "")}"
              data-ticket-status="${this._escapeHtml(t.status || "")}"
              data-ticket-priority="${this._escapeHtml(t.priority || "")}">
        <div class="flex-1 min-w-0">
          <p class="text-[11px] font-mono text-muted-foreground">${this._escapeHtml(t.ticket_number || "")}</p>
          <p class="text-sm text-foreground group-hover:text-primary transition-colors truncate">${this._escapeHtml(t.subject || "")}</p>
          <p class="text-[11px] text-muted-foreground mt-0.5 capitalize">${this._escapeHtml(t.status || "")}</p>
        </div>
      </button>
    `,
      )
      .join("")

    this.suggestionsTarget.innerHTML = `
      <p class="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mb-1">Similar tickets</p>
      <div class="divide-y divide-border/50">${html}</div>
    `
    this.suggestionsTarget.classList.remove("hidden")
  }

  async _performSearch(query) {
    this.loadingTarget.classList.remove("hidden")
    this.resultsTarget.classList.add("hidden")

    try {
      const url = `${this.searchUrlValue}/search?q=${encodeURIComponent(query)}`
      const response = await get(url, { responseKind: "json" })

      if (!response.ok) throw new Error("Search failed")

      const tickets = await response.json
      this._renderResults(tickets)
    } catch {
      this.resultsTarget.innerHTML = `
        <p class="text-xs text-muted-foreground py-2">Could not search tickets. Try entering a ticket number directly.</p>
      `
      this.resultsTarget.classList.remove("hidden")
    } finally {
      this.loadingTarget.classList.add("hidden")
    }
  }

  _renderResults(tickets) {
    // Filter out current ticket
    const filtered = tickets.filter(
      (t) =>
        t.id !== this.currentIdValue &&
        t.id?.toString() !== this.currentIdValue,
    )

    if (filtered.length === 0) {
      this.resultsTarget.innerHTML = `
        <p class="text-xs text-muted-foreground py-2">No matching tickets found.</p>
      `
      this.resultsTarget.classList.remove("hidden")
      return
    }

    const html = filtered
      .map(
        (t) => `
      <button type="button"
              class="w-full flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-muted/60 transition-colors text-left group"
              data-action="click->support--merge-ticket#selectTarget"
              data-ticket-id="${t.id}"
              data-ticket-number="${this._escapeHtml(t.ticket_number || "")}"
              data-ticket-subject="${this._escapeHtml(t.subject || "")}"
              data-ticket-status="${this._escapeHtml(t.status || "")}"
              data-ticket-priority="${this._escapeHtml(t.priority || "")}">
        <div class="flex-1 min-w-0">
          <p class="text-[11px] font-mono text-muted-foreground">${this._escapeHtml(t.ticket_number || "")}</p>
          <p class="text-sm text-foreground group-hover:text-primary transition-colors truncate">${this._escapeHtml(t.subject || "")}</p>
          <p class="text-[11px] text-muted-foreground mt-0.5 capitalize">${this._escapeHtml(t.status || "")}</p>
        </div>
      </button>
    `,
      )
      .join("")

    this.resultsTarget.innerHTML = `<div class="divide-y divide-border/50">${html}</div>`
    this.resultsTarget.classList.remove("hidden")
  }

  selectTarget(event) {
    const btn = event.currentTarget
    const id = btn.dataset.ticketId
    const number = btn.dataset.ticketNumber
    const subject = btn.dataset.ticketSubject
    const status = btn.dataset.ticketStatus
    const priority = btn.dataset.ticketPriority

    this._selectedId = id
    this.targetIdTarget.value = id
    this.confirmBtnTarget.disabled = false

    // Show preview
    this.previewNumberTarget.textContent = number
    this.previewSubjectTarget.textContent = subject
    this.previewMetaTarget.textContent =
      [status, priority].filter(Boolean).join(" \u00b7 ") || ""
    this.previewTarget.classList.remove("hidden")

    // Hide search results and suggestions
    this.resultsTarget.classList.add("hidden")
    if (this.hasSuggestionsTarget)
      this.suggestionsTarget.classList.add("hidden")
  }

  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
